import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { gpPercent } from '@kdental/shared';
import { api, ApiError, type CustomerContact, type CustomerShipTo, type OrderShipToBody } from '../lib/api.js';
import { useCart, type SelectedCustomer } from '../lib/cart-store.js';
import { useOutbox } from '../lib/outbox-store.js';
import { useConnectivity } from '../lib/connectivity.js';
import { useAuth } from '../lib/auth-store.js';
import { mapsUrl, money2 } from '../lib/format.js';
import { toast } from '../lib/toast-store.js';
import { IconPin, IconSearch } from '../components/icons.js';

type PickedCustomer = SelectedCustomer;

function isRealContact(c: CustomerContact): boolean {
  return Boolean(c.name || c.phone || c.email);
}
type OrderType = 'order' | 'quote';
/** A line's price as estimated in the cart vs. what Spire actually priced. */
type PriceDelta = { partNo: string; sellMeasure: string | null; estimated: string; final: string };
type Placed =
  | {
      orderNo: string;
      type: OrderType;
      finalTotal?: string;
      deltas?: PriceDelta[];
      /** Lines Spire priced at $0 — no price exists for that customer/UOM. */
      zeroLines?: { partNo: string; sellMeasure: string | null }[];
    }
  | { queued: true; type: OrderType };

export function CartScreen() {
  const items = useCart((s) => s.items);
  const setQty = useCart((s) => s.setQty);
  const setPrice = useCart((s) => s.setPrice);
  const remove = useCart((s) => s.remove);
  const clear = useCart((s) => s.clear);
  const enqueue = useOutbox((s) => s.enqueue);
  const bridgeOnline = useConnectivity((s) => s.bridgeOnline);
  const canViewMargin = useAuth((s) => s.user?.canViewMargin ?? false);
  const canOverride = useAuth((s) => s.user?.canOverridePrice ?? true);
  const canPlaceOrders = useAuth((s) => s.user?.canPlaceOrders ?? true);
  const settings = useQuery({ queryKey: ['settings'], queryFn: ({ signal }) => api.settings.get(signal) }).data;
  const minMargin = settings?.minMarginPct ?? 0;

  const customer = useCart((s) => s.selectedCustomer);
  const setSelectedCustomer = useCart((s) => s.setSelectedCustomer);
  const clearSelectedCustomer = useCart((s) => s.clearSelectedCustomer);
  const [shipTo, setShipTo] = useState<CustomerShipTo | null>(null);
  const [shipContact, setShipContact] = useState<CustomerContact | null>(null);
  const [po, setPo] = useState('');
  const [requiredDate, setRequiredDate] = useState('');
  const [notes, setNotes] = useState('');
  // Quotes-only users are locked to quotes (server enforces too).
  const [orderType, setOrderType] = useState<OrderType>(canPlaceOrders ? 'order' : 'quote');
  const [review, setReview] = useState(false);
  const [placed, setPlaced] = useState<Placed | null>(null);

  const subtotal = items.reduce((sum, i) => sum + Number(i.unitPrice) * i.qty, 0);
  // Lines with no price estimate — Spire will likely price these $0 (no
  // matrix rule / ladder for the UOM; Spire doesn't prorate across UOMs).
  const zeroEstimateCount = items.filter((i) => Number(i.unitPrice) <= 0).length;
  // Margin-floor policy: 'block' disables checkout while any computable line
  // sits below the floor (price-override users are exempt).
  const belowFloorCount =
    minMargin > 0 && canViewMargin
      ? items.filter((i) => {
          if (!i.unitCost) return false;
          const gp = gpPercent(i.unitPrice, i.unitCost);
          return gp !== null && gp.lt(minMargin);
        }).length
      : 0;
  const marginBlocked = settings?.minMarginEnforce === 'block' && !canOverride && belowFloorCount > 0;

  // Once a customer is picked, load their full record for ship-to addresses.
  const fullCustomer = useQuery({
    queryKey: ['customer', customer?.spireId],
    queryFn: ({ signal }) => api.customers.getById(customer!.spireId, signal),
    enabled: customer != null,
  });
  const shipTos = fullCustomer.data?.shipTos ?? [];

  const pickCustomer = (c: PickedCustomer) => {
    setSelectedCustomer(c);
    setShipTo(null);
    setShipContact(null);
  };
  const clearCustomer = () => {
    clearSelectedCustomer();
    setShipTo(null);
    setShipContact(null);
  };
  const selectShipTo = (s: CustomerShipTo) => {
    setShipTo(s);
    setShipContact(s.contacts.find(isRealContact) ?? null);
  };

  // Default the ship-to to the customer's default (or first) once detail loads.
  useEffect(() => {
    if (!fullCustomer.data || shipTo !== null) return;
    const def =
      fullCustomer.data.shipTos.find((s) => s.isDefault) ?? fullCustomer.data.shipTos[0] ?? null;
    if (def) selectShipTo(def);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullCustomer.data]);

  const checkout = useMutation({
    mutationFn: async (): Promise<Placed> => {
      if (!customer) throw new Error('Pick a customer first.');
      const id = crypto.randomUUID();
      const lines = items.map((i) => ({
        partNo: i.partNo,
        whse: i.whseCode,
        description: i.description ?? undefined,
        orderQty: String(i.qty),
        // Only overridden prices post to Spire (as userPrice). Untouched lines
        // omit the price so Spire's pricing engine (price matrix, price level,
        // promos) prices them — the cart price is just the display estimate.
        unitPrice: i.priceOverridden ? i.unitPrice : undefined,
        sellMeasure: i.sellMeasure,
      }));
      const shipToBody: OrderShipToBody | undefined = shipTo
        ? {
            shipId: shipTo.shipId ?? undefined,
            name: shipTo.name ?? undefined,
            line1: shipTo.line1 ?? undefined,
            line2: shipTo.line2 ?? undefined,
            city: shipTo.city ?? undefined,
            provState: shipTo.provState ?? undefined,
            postalCode: shipTo.postalCode ?? undefined,
            country: shipTo.country ?? undefined,
            contactName: shipContact?.name ?? undefined,
            contactEmail: shipContact?.email ?? undefined,
            contactPhone: shipContact?.phone ?? undefined,
          }
        : undefined;
      const body = {
        customerNo: customer.customerNo,
        customerPO: po.trim() || undefined,
        requiredDate: requiredDate || undefined,
        type: orderType,
        shipTo: shipToBody,
        notes: notes.trim() || undefined,
        items: lines,
      };
      const queue = () =>
        enqueue({
          id,
          customerNo: customer.customerNo,
          customerName: customer.name,
          customerPO: body.customerPO,
          requiredDate: body.requiredDate,
          type: orderType,
          shipTo: shipToBody,
          notes: body.notes,
          items: lines,
          createdAt: Date.now(),
        });

      if (!navigator.onLine || !bridgeOnline) {
        queue();
        return { queued: true, type: orderType };
      }
      try {
        const order = await api.salesOrders.create(body, id);
        // Reconcile: Spire's engine priced the untouched lines — surface any
        // line whose final price differs from the cart estimate.
        const deltas: PriceDelta[] = [];
        for (const i of items) {
          const match = order.items.find(
            (l) =>
              l.partNo === i.partNo &&
              (i.sellMeasure == null || l.sellMeasure == null || l.sellMeasure === i.sellMeasure),
          );
          if (!match) continue;
          if (Math.abs(Number(match.unitPrice) - Number(i.unitPrice)) > 0.005) {
            deltas.push({
              partNo: i.partNo,
              sellMeasure: match.sellMeasure,
              estimated: i.unitPrice,
              final: match.unitPrice,
            });
          }
        }
        const finalTotal = Number(order.totalOrdered) > 0 ? order.totalOrdered : order.subtotal;
        // Validated on mil (2026-08-24): Spire does NOT prorate matrix rules
        // across UOMs — a UOM with no rule and no ladder posts at $0. Surface
        // those loudly so the rep fixes the order instead of shipping free stock.
        const zeroLines = order.items
          .filter((l) => l.partNo.trim() !== '' && Number(l.orderQty) > 0 && Number(l.unitPrice) <= 0)
          .map((l) => ({ partNo: l.partNo, sellMeasure: l.sellMeasure }));
        return { orderNo: order.orderNo, type: orderType, finalTotal, deltas, zeroLines };
      } catch (err) {
        if (err instanceof ApiError && (err.status === 0 || err.status >= 500)) {
          queue();
          return { queued: true, type: orderType };
        }
        throw err;
      }
    },
    onSuccess: (res) => {
      setReview(false);
      setPlaced(res);
      clear();
    },
  });

  if (placed) {
    const queued = 'queued' in placed;
    const noun = placed.type === 'quote' ? 'Quote' : 'Order';
    return (
      <>
        <div className="topbar">
          <h2>{queued ? `${noun} queued` : `${noun} placed`}</h2>
        </div>
        <div className="confirm">
          <div className="check-circle" style={queued ? { background: '#fff1d9', color: '#a8770a' } : undefined}>
            {queued ? '⏱' : '✓'}
          </div>
          <h2>{queued ? 'Saved — will send when online' : `${noun} placed`}</h2>
          <p>
            {queued
              ? "You're offline. It's queued and posts to Spire automatically when the bridge is back — exactly once."
              : `Created in Spire as a ${placed.type === 'quote' ? 'quote' : 'desk-entered order'}.`}
          </p>
          {!queued && 'orderNo' in placed && <div className="order-num">{placed.orderNo}</div>}
          {!queued && 'finalTotal' in placed && placed.finalTotal && (
            <div className="kd-muted" style={{ marginTop: 6, fontSize: 13 }}>
              Spire total: <strong>{money2(placed.finalTotal)}</strong>
            </div>
          )}
          {!queued && 'zeroLines' in placed && placed.zeroLines && placed.zeroLines.length > 0 && (
            <div
              className="kd-card"
              style={{ marginTop: 14, textAlign: 'left', width: '100%', fontSize: 13, border: '1px solid var(--kd-danger)' }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--kd-danger)' }}>
                ⚠ {placed.zeroLines.length} line{placed.zeroLines.length > 1 ? 's' : ''} priced at $0.00
              </div>
              <div className="kd-muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
                Spire has no price for this customer / unit of measure. Fix the price on the{' '}
                <Link to="/orders">Orders tab</Link> before this {placed.type === 'quote' ? 'quote' : 'order'} ships.
              </div>
              {placed.zeroLines.map((l) => (
                <div key={`${l.partNo}|${l.sellMeasure ?? ''}`} style={{ padding: '3px 0' }}>
                  {l.partNo}
                  {l.sellMeasure ? ` · ${l.sellMeasure}` : ''}
                </div>
              ))}
            </div>
          )}
          {!queued && 'deltas' in placed && placed.deltas && placed.deltas.length > 0 && (
            <div
              className="kd-card"
              style={{ marginTop: 14, textAlign: 'left', width: '100%', fontSize: 13 }}
            >
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Spire adjusted {placed.deltas.length} price{placed.deltas.length > 1 ? 's' : ''}
              </div>
              <div className="kd-muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
                Contract / price-level rules in Spire set the final price for these lines.
              </div>
              {placed.deltas.map((d) => (
                <div
                  key={`${d.partNo}|${d.sellMeasure ?? ''}`}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '3px 0' }}
                >
                  <span>
                    {d.partNo}
                    {d.sellMeasure ? ` · ${d.sellMeasure}` : ''}
                  </span>
                  <span>
                    <s className="kd-muted">{money2(d.estimated)}</s> → <strong>{money2(d.final)}</strong>
                  </span>
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 28, display: 'flex', gap: 10, width: '100%' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setPlaced(null);
                clearCustomer();
                setPo('');
                setRequiredDate('');
                setNotes('');
                setOrderType('order');
              }}
            >
              New order
            </button>
            <Link to="/clients" className="btn btn-secondary" style={{ textAlign: 'center', textDecoration: 'none', lineHeight: '1.4' }}>
              Clients
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="topbar">
        <h2>Cart</h2>
        {items.length > 0 && (
          <button type="button" className="change" onClick={clear} style={{ fontSize: 13 }}>
            Clear
          </button>
        )}
      </div>

      <QuickAdd />

      {items.length === 0 ? (
        <div className="kd-muted" style={{ padding: 18 }}>
          Your cart is empty. Add items from inventory or re-order from a client's history.
        </div>
      ) : (
        <>
          {customer ? (
            <>
              <div className="cart-summary">
                <div>
                  <div className="for">Billing to</div>
                  <div className="name">{customer.name}</div>
                </div>
                <button type="button" className="change" onClick={clearCustomer}>
                  Change
                </button>
              </div>
              {fullCustomer.isLoading && (
                <div className="kd-muted" style={{ padding: '0 18px 8px' }}>Loading ship-to addresses…</div>
              )}
              {shipTos.length > 0 && (
                <ShipToSelector
                  shipTos={shipTos}
                  selected={shipTo}
                  contact={shipContact}
                  onSelect={selectShipTo}
                  onContact={setShipContact}
                />
              )}
            </>
          ) : (
            <CustomerPicker onPick={pickCustomer} />
          )}

          {items.map((i) => {
            const lineTotal = Number(i.unitPrice) * i.qty;
            const gp = i.unitCost && canViewMargin ? gpPercent(i.unitPrice, i.unitCost) : null;
            return (
              <div key={i.key} className="cart-line">
                <div className="body">
                  <div className="name">{i.description ?? i.partNo}</div>
                  <div className="sku">
                    {i.partNo} @ {i.whseCode}
                    {i.sellMeasure ? ` · ${i.sellMeasure}` : ''}
                    {i.priceOverridden
                      ? ' · custom price'
                      : i.priceSource === 'contract'
                        ? ' · contract price'
                        : ''}
                    {Number(i.unitPrice) <= 0 && (
                      <span style={{ color: 'var(--kd-danger)', fontWeight: 700 }}> · no price</span>
                    )}
                  </div>
                  <div className="row">
                    <div className="mini-stepper">
                      <button type="button" onClick={() => setQty(i.key, i.qty - 1)}>−</button>
                      <span className="v">{i.qty}</span>
                      <button type="button" onClick={() => setQty(i.key, i.qty + 1)}>+</button>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 13 }}>
                      $
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={i.unitPrice}
                        readOnly={!canOverride}
                        onChange={(e) => canOverride && setPrice(i.key, e.target.value)}
                        style={{ width: 72, padding: '5px 8px', border: '1px solid var(--kd-line)', borderRadius: 8, fontSize: 13 }}
                      />
                    </label>
                    <span className="total">{money2(lineTotal)}</span>
                  </div>
                  {gp && (() => {
                    const below = (gp.lt(0) || (minMargin > 0 && gp.lt(minMargin)));
                    return (
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            borderRadius: 999,
                            padding: '2px 8px',
                            background: below ? '#fce4e1' : '#e0f4e9',
                            color: below ? 'var(--kd-danger)' : 'var(--kd-green-dark)',
                          }}
                        >
                          {gp.toFixed(1)}% GP
                        </span>
                        {minMargin > 0 && gp.lt(minMargin) && !gp.lt(0) && (
                          <span className="kd-muted" style={{ fontSize: 11 }}>below {minMargin}% floor</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <button
                  type="button"
                  onClick={() => remove(i.key)}
                  style={{ background: 'none', border: 'none', color: 'var(--kd-danger)', cursor: 'pointer', fontSize: 16 }}
                >
                  ✕
                </button>
              </div>
            );
          })}

          <div className="kd-card">
            <div className="for" style={{ marginBottom: 8 }}>
              Order details
            </div>
            <div className="filter-chips" style={{ padding: '0 0 10px' }}>
              {canPlaceOrders && (
                <button type="button" className={`chip${orderType === 'order' ? ' active' : ''}`} onClick={() => setOrderType('order')}>
                  Order
                </button>
              )}
              <button type="button" className={`chip${orderType === 'quote' ? ' active' : ''}`} onClick={() => setOrderType('quote')}>
                Quote
              </button>
              {!canPlaceOrders && (
                <span className="kd-muted" style={{ fontSize: 11.5, alignSelf: 'center' }}>
                  Your account creates quotes only
                </span>
              )}
            </div>
            <label className="kd-muted" style={{ display: 'block', marginBottom: 8 }}>
              Customer PO
              <input
                value={po}
                onChange={(e) => setPo(e.target.value)}
                placeholder="optional"
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '9px 12px', border: '1px solid var(--kd-line)', borderRadius: 10, fontSize: 14 }}
              />
            </label>
            <label className="kd-muted" style={{ display: 'block', marginBottom: 8 }}>
              Required by
              <input
                type="date"
                value={requiredDate}
                onChange={(e) => setRequiredDate(e.target.value)}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '9px 12px', border: '1px solid var(--kd-line)', borderRadius: 10, fontSize: 14 }}
              />
            </label>
            <label className="kd-muted" style={{ display: 'block' }}>
              Order notes
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="optional — added to the order"
                rows={3}
                maxLength={2000}
                style={{ display: 'block', width: '100%', marginTop: 4, padding: '9px 12px', border: '1px solid var(--kd-line)', borderRadius: 10, fontSize: 14, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </label>
          </div>

          <div className="cart-totals">
            <div className="total-row">
              <span>{items.length} line{items.length > 1 ? 's' : ''} · subtotal</span>
              <span>{money2(subtotal)}</span>
            </div>
            <div className="total-row grand">
              <span>Subtotal</span>
              <span className="amt">{money2(subtotal)}</span>
            </div>
            <div className="kd-muted" style={{ fontSize: 11.5, marginTop: 4 }}>
              Taxes &amp; freight are calculated in Spire when the order is placed.
            </div>
            {zeroEstimateCount > 0 && (
              <div
                style={{
                  marginTop: 8,
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: '#fce4e1',
                  color: 'var(--kd-danger)',
                  fontSize: 12.5,
                  fontWeight: 600,
                }}
              >
                ⚠ {zeroEstimateCount} line{zeroEstimateCount > 1 ? 's have' : ' has'} no price —
                Spire will post {zeroEstimateCount > 1 ? 'them' : 'it'} at $0.00 unless a contract
                rule applies. Set a price or switch the unit of measure.
              </div>
            )}
          </div>

          {marginBlocked && (
            <div style={{ margin: '0 18px 8px', padding: '8px 10px', borderRadius: 8, background: '#fce4e1', color: 'var(--kd-danger)', fontSize: 12.5, fontWeight: 600 }}>
              {belowFloorCount} line{belowFloorCount > 1 ? 's are' : ' is'} below the {minMargin}% margin floor - checkout is blocked by policy. Raise the price or remove the line.
            </div>
          )}
          <button
            type="button"
            className="place-order-btn"
            disabled={!customer || marginBlocked}
            onClick={() => setReview(true)}
            style={{ opacity: !customer || marginBlocked ? 0.6 : 1 }}
          >
            {customer ? `Review ${orderType === 'quote' ? 'quote' : 'order'}` : 'Pick a customer first'}
          </button>
        </>
      )}

      {review && customer && (
        <ReviewSheet
          customerName={customer.name}
          shipToLabel={shipTo ? shipTo.name || shipTo.shipId || null : null}
          orderType={orderType}
          po={po}
          requiredDate={requiredDate}
          notes={notes.trim()}
          lineCount={items.length}
          zeroPriceCount={zeroEstimateCount}
          total={subtotal}
          pending={checkout.isPending}
          error={checkout.isError ? checkout.error.message : null}
          onConfirm={() => checkout.mutate()}
          onClose={() => setReview(false)}
        />
      )}
    </>
  );
}

function ReviewSheet(props: {
  customerName: string;
  shipToLabel: string | null;
  orderType: OrderType;
  po: string;
  requiredDate: string;
  notes: string;
  lineCount: number;
  zeroPriceCount: number;
  total: number;
  pending: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <div
      onClick={props.onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,37,58,.5)', zIndex: 50, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: '#fff', width: '100%', maxWidth: 480, borderRadius: '18px 18px 0 0', padding: '20px 20px calc(20px + env(safe-area-inset-bottom, 0px))' }}
      >
        <h3 style={{ margin: '0 0 12px', color: 'var(--kd-navy)' }}>
          Review {props.orderType === 'quote' ? 'quote' : 'order'}
        </h3>
        <ReviewRow label="Type" value={props.orderType === 'quote' ? 'Quote' : 'Order'} />
        <ReviewRow label="Customer" value={props.customerName} />
        {props.shipToLabel && <ReviewRow label="Ship to" value={props.shipToLabel} />}
        {props.po && <ReviewRow label="PO" value={props.po} />}
        {props.requiredDate && <ReviewRow label="Required by" value={props.requiredDate} />}
        {props.notes && <ReviewRow label="Notes" value={props.notes.length > 60 ? props.notes.slice(0, 60) + '…' : props.notes} />}
        <ReviewRow label="Lines" value={String(props.lineCount)} />
        <ReviewRow label="Total" value={money2(props.total)} strong />

        {props.zeroPriceCount > 0 && (
          <div
            style={{
              marginTop: 10,
              padding: '8px 10px',
              borderRadius: 8,
              background: '#fce4e1',
              color: 'var(--kd-danger)',
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            ⚠ {props.zeroPriceCount} line{props.zeroPriceCount > 1 ? 's' : ''} with no price —
            will post at $0.00 unless a Spire contract rule applies.
          </div>
        )}

        {props.error && <div className="kd-error" style={{ margin: '10px 0 0' }}>{props.error}</div>}

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button type="button" className="btn btn-secondary" onClick={props.onClose} disabled={props.pending}>
            Back
          </button>
          <button type="button" className="btn btn-primary" onClick={props.onConfirm} disabled={props.pending}>
            {props.pending ? 'Placing…' : `Confirm & place`}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 14, borderBottom: '1px solid var(--kd-line)' }}>
      <span className="kd-muted">{label}</span>
      <span style={{ color: 'var(--kd-text)', fontWeight: strong ? 700 : 500 }}>{value}</span>
    </div>
  );
}

function QuickAdd() {
  const add = useCart((s) => s.add);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async () => {
    const rows = text
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.split(/[\s,]+/);
        return { partNo: parts[0]!, qty: Math.max(1, Math.round(Number(parts[1])) || 1) };
      })
      .filter((r) => r.partNo);
    if (rows.length === 0) return;
    setBusy(true);
    let added = 0;
    const missing: string[] = [];
    for (const r of rows) {
      try {
        const res = await api.inventory.search({ q: r.partNo, limit: 10 });
        const hit =
          res.find((i) => i.partNo === r.partNo) ??
          res.find((i) => i.partNo.toUpperCase() === r.partNo.toUpperCase());
        if (hit) {
          add({
            partNo: hit.partNo,
            whseCode: hit.whseCode,
            description: hit.description,
            unitPrice: hit.defaultSellPrice,
            ...(hit.uom ? { sellMeasure: hit.uom } : {}),
            qty: r.qty,
          });
          added += 1;
        } else {
          missing.push(r.partNo);
        }
      } catch {
        missing.push(r.partNo);
      }
    }
    setBusy(false);
    setText('');
    setOpen(false);
    toast(
      `Added ${added} item${added === 1 ? '' : 's'}${missing.length ? ` · not found: ${missing.join(', ')}` : ''}`,
      missing.length ? 'error' : 'success',
    );
  };

  if (!open) {
    return (
      <div style={{ padding: '8px 14px 0' }}>
        <button type="button" className="show-more" style={{ margin: 0, width: '100%' }} onClick={() => setOpen(true)}>
          ⚡ Quick add by part #
        </button>
      </div>
    );
  }
  return (
    <div style={{ padding: '8px 14px 0' }}>
      <div className="kd-card">
        <div className="for" style={{ marginBottom: 6 }}>
          Quick add — one per line: <span className="kd-muted">PART QTY</span>
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          placeholder={'7022 5\n1234 2'}
          style={{ display: 'block', width: '100%', padding: '9px 12px', border: '1px solid var(--kd-line)', borderRadius: 10, fontSize: 14, resize: 'vertical', fontFamily: 'monospace' }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => { setOpen(false); setText(''); }}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={busy || !text.trim()} onClick={() => void run()}>
            {busy ? 'Adding…' : 'Add to cart'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShipToSelector({
  shipTos,
  selected,
  contact,
  onSelect,
  onContact,
}: {
  shipTos: CustomerShipTo[];
  selected: CustomerShipTo | null;
  contact: CustomerContact | null;
  onSelect: (s: CustomerShipTo) => void;
  onContact: (c: CustomerContact | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ql = q.trim().toLowerCase();
  const filtered = ql
    ? shipTos.filter((s) =>
        [s.name, s.shipId, s.city, s.provState].some((v) => (v ?? '').toLowerCase().includes(ql)),
      )
    : shipTos;
  const shown = filtered.slice(0, 25);
  const sel = selected;
  const contacts = (sel?.contacts ?? []).filter(isRealContact);
  const selIdx = sel ? sel.contacts.findIndex((c) => c === contact) : -1;
  const addr = (s: CustomerShipTo) => [s.line1, s.city, s.provState, s.postalCode].filter(Boolean).join(', ') || '—';

  return (
    <div className="kd-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="for">Ship to</div>
        {shipTos.length > 1 && (
          <button type="button" className="change" onClick={() => setOpen((o) => !o)}>
            {open ? 'Done' : 'Change'}
          </button>
        )}
      </div>

      {!open && sel && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontWeight: 600 }}>
            {sel.name || sel.shipId || 'Ship-to'}
            {sel.isDefault ? ' · default' : ''}
          </div>
          <div className="kd-muted" style={{ fontSize: 12.5 }}>{addr(sel)}</div>
          {(() => {
            const u = mapsUrl(sel.line1, sel.city, sel.provState, sel.postalCode, sel.country);
            return u ? (
              <a className="map-link" href={u} target="_blank" rel="noopener noreferrer">
                <IconPin size={13} /> Directions
              </a>
            ) : null;
          })()}
        </div>
      )}

      {open && (
        <div style={{ marginTop: 8 }}>
          {shipTos.length > 8 && (
            <div className="search-wrap" style={{ marginBottom: 8 }}>
              <IconSearch size={16} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ship-tos" />
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflowY: 'auto' }}>
            {shown.map((s, i) => (
              <button
                key={s.shipId ?? i}
                type="button"
                className={`shipto-option${s === sel ? ' active' : ''}`}
                onClick={() => {
                  onSelect(s);
                  setOpen(false);
                  setQ('');
                }}
              >
                <div style={{ fontWeight: 600 }}>
                  {s.name || s.shipId || 'Ship-to'}
                  {s.isDefault ? ' · default' : ''}
                </div>
                <div className="kd-muted" style={{ fontSize: 12 }}>{addr(s)}</div>
              </button>
            ))}
          </div>
          {filtered.length > shown.length && (
            <div className="kd-muted" style={{ fontSize: 12, marginTop: 6 }}>
              +{filtered.length - shown.length} more — search to narrow.
            </div>
          )}
          {ql && filtered.length === 0 && (
            <div className="kd-muted" style={{ fontSize: 12, marginTop: 6 }}>No matches.</div>
          )}
        </div>
      )}

      {sel && contacts.length > 0 && (
        <label className="kd-muted" style={{ display: 'block', marginTop: 10 }}>
          Ship-to contact
          <select
            value={String(selIdx)}
            onChange={(e) => {
              const idx = Number(e.target.value);
              onContact(idx >= 0 ? sel.contacts[idx]! : null);
            }}
            style={{ display: 'block', width: '100%', marginTop: 4, padding: '9px 12px', border: '1px solid var(--kd-line)', borderRadius: 10, fontSize: 14, background: '#fff' }}
          >
            <option value="-1">None</option>
            {sel.contacts.map((c, idx) =>
              isRealContact(c) ? (
                <option key={idx} value={idx}>
                  {c.name || c.email || c.phone}
                </option>
              ) : null,
            )}
          </select>
        </label>
      )}
    </div>
  );
}

function CustomerPicker({ onPick }: { onPick: (c: PickedCustomer) => void }) {
  const [q, setQ] = useState('');
  const [activeQ, setActiveQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setActiveQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const results = useQuery({
    queryKey: ['customers', 'picker', activeQ],
    queryFn: ({ signal }) => api.customers.search(activeQ ? { q: activeQ } : {}, signal),
  });

  return (
    <div className="kd-card">
      <div className="for" style={{ marginBottom: 8 }}>
        Bill to which client?
      </div>
      <div className="search-wrap" style={{ marginBottom: 8 }}>
        <IconSearch size={16} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your clients" />
      </div>
      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
        {results.data?.slice(0, 15).map((c) => (
          <button
            key={c.spireId}
            type="button"
            onClick={() => onPick({ spireId: c.spireId, customerNo: c.customerNo, name: c.name, sellLevel: c.sellLevel })}
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
              gap: 8,
              padding: '9px 6px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              borderBottom: '1px solid var(--kd-line)',
              textAlign: 'left',
            }}
          >
            <span style={{ color: 'var(--kd-text)', fontSize: 14 }}>{c.name}</span>
            <span style={{ color: 'var(--kd-text-3)', fontSize: 12, fontFamily: 'monospace' }}>{c.customerNo}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
